import { FEATURES } from '../../app/terminology/features';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { fieldValue, usePolarisEvents } from '../../app/components/polaris';
import {
    CoverageFilter,
    FitmentCoverageHealthReport,
    FitmentCoverageHealthVehicle,
    StorefrontSearchReport,
    StorefrontSearchReportTopSearch,
} from '../../app/models/Reports';
import {
    formatSearchDateLabel,
    SearchActivityChart,
} from './SearchActivitySummary';
import './ReportsPage.css';
import VehicleReportDetails, { getSearchLabel, pageSizeOptions } from './VehicleReportDetails';
import { csvSharePercent, formatSharePercent, getSharePercent } from './reportShare';
import PlanFeatureStatus from '../plan/PlanFeatureStatus';

type ReportRange = '7' | '30' | '90' | 'custom';
type ComparisonPeriod =
    | 'none'
    | 'previous_period'
    | 'previous_year'
    | 'previous_year_weekday'
    | 'custom';
type ComparisonDateRange = {
    start: Date;
    end: Date;
};
type DailyBreakdownRow = {
    currentDate: string;
    currentSearches: number;
    currentDataAvailable: boolean;
    comparisonDate?: string;
    comparisonSearches?: number;
    comparisonDataAvailable?: boolean;
};
type ReportKind =
    | 'daily_search_volume'
    | 'top_vehicle_searches'
    | 'coverage_opportunities'
    | 'fitment_coverage_health';
type VehicleSort = 'searches' | 'increase' | 'decrease';

type ReportDefinition = {
    value: ReportKind;
    label: string;
};

const reportDefinitions: ReportDefinition[] = [
    {
        value: 'daily_search_volume',
        label: 'Daily search volume',
    },
    {
        value: 'top_vehicle_searches',
        label: 'Top vehicle searches',
    },
    {
        value: 'coverage_opportunities',
        label: 'Coverage opportunities',
    },
    {
        value: 'fitment_coverage_health',
        label: 'Fitment coverage health',
    },
];

const reportOptions = reportDefinitions.map((definition) => ({
    label: definition.label,
    value: definition.value,
}));

const getInitialReport = (): ReportKind => {
    const requestedReport = new URLSearchParams(window.location.search).get('report');
    if (requestedReport === 'search_demand_trends') return 'top_vehicle_searches';

    // Preserve older dashboard/deep links that pointed to the combined
    // Search activity report before its two views became standalone reports.
    if (requestedReport === 'search_activity') {
        return 'daily_search_volume';
    }

    return reportDefinitions.some((definition) => definition.value === requestedReport)
        ? requestedReport as ReportKind
        : 'daily_search_volume';
};

const rangeOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Custom range', value: 'custom' },
];

const comparisonOptions = [
    { label: 'No comparison', value: 'none' },
    { label: 'Previous period', value: 'previous_period' },
    { label: 'Previous year', value: 'previous_year' },
    { label: 'Previous year (match day of week)', value: 'previous_year_weekday' },
    { label: 'Custom period', value: 'custom' },
];

const comparisonReportKinds = new Set<ReportKind>([
    'daily_search_volume',
    'top_vehicle_searches',
]);

const toInputDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const initialToDate = toInputDate(new Date());
const initialFromDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() - 29);
    return toInputDate(date);
})();

const shiftInputDate = (value: string, days: number) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    date.setDate(date.getDate() + days);
    return toInputDate(date);
};

const parseInputDate = (value: string) => {
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? new Date() : date;
};

const formatPickerDate = (date: Date) => date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

const formatComparisonRangeLabel = (from: string, to: string) => {
    const fromValue = parseInputDate(from);
    const toValue = parseInputDate(to);

    return `${fromValue.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })} – ${toValue.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })}`;
};

// "Sep 22 – Oct 21, 2026", with both years only when they differ.
const formatButtonRange = (from: string, to: string) => {
    const fromValue = parseInputDate(from);
    const toValue = parseInputDate(to);
    const sameYear = fromValue.getFullYear() === toValue.getFullYear();
    const start = fromValue.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
    const end = toValue.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} – ${end}`;
};

const getInputDateRangeDays =(from: string, to: string) => {
    const fromValue = new Date(`${from}T00:00:00`);
    const toValue = new Date(`${to}T00:00:00`);
    if (Number.isNaN(fromValue.getTime()) || Number.isNaN(toValue.getTime())) return 0;
    return Math.round((toValue.getTime() - fromValue.getTime()) / 86400000) + 1;
};

const getInitialDateRange = () => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from') ?? '';
    const to = params.get('to') ?? '';
    const isCalendarDate = (value: string) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
        const date = new Date(`${value}T00:00:00Z`);
        return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
    };
    const hasDateRange = isCalendarDate(from) && isCalendarDate(to) && from <= to;
    const fromDate = hasDateRange ? from : initialFromDate;
    const toDate = hasDateRange ? to : initialToDate;
    const comparisonTo = shiftInputDate(fromDate, -1);
    return {
        range: (hasDateRange ? 'custom' : '30') as ReportRange,
        from: fromDate,
        to: toDate,
        comparisonFrom: shiftInputDate(fromDate, -getInputDateRangeDays(fromDate, toDate)),
        comparisonTo,
    };
};

const formatCount = (value?: number) => (value ?? 0).toLocaleString();

const formatDisplayDate = (value?: string) => {
    if (!value) return '-';

    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return '-';

    return parsed.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatDateOnly = (value?: string) => value?.slice(0, 10) || '-';

const isSearchDataAvailableOn = (
    report: StorefrontSearchReport,
    date?: string
) => {
    if (!date) return false;
    // If an older API response does not include an availability boundary, do not
    // assume missing aggregate rows are real zero-search days.
    if (report.dataAvailableFrom === undefined) return false;

    const dataAvailableFrom = report.dataAvailableFrom?.slice(0, 10);
    return Boolean(dataAvailableFrom && date.slice(0, 10) >= dataAvailableFrom);
};

const isComparisonDataComplete = (report?: StorefrontSearchReport) => {
    if (!report?.comparisonEnabled) return true;
    if (report.dataAvailableFrom === undefined) return false;

    const dataAvailableFrom = report.dataAvailableFrom?.slice(0, 10);
    return Boolean(dataAvailableFrom && report.previousFrom.slice(0, 10) >= dataAvailableFrom);
};

const formatFieldName = (key: string) => key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const escapeCsvCell = (value: string | number) => {
    let text = String(value ?? '');
    if (/^[=+\-@]/.test(text)) {
        text = `'${text}`;
    }

    return `"${text.replace(/"/g, '""')}"`;
};

const downloadDailyVolumeCsv = (report: StorefrontSearchReport) => {
    const headings = report.comparisonEnabled
        ? ['Date', 'Searches', 'Comparison date', 'Comparison searches']
        : ['Date', 'Searches'];
    const currentCounts = new Map(
        report.dailyCounts.map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const comparisonCounts = new Map(
        (report.previousDailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const periodDays = getInclusiveDayCount(report);
    const rows = Array.from({ length: periodDays }, (_, index) => {
        const currentDate = shiftInputDate(formatDateOnly(report.from), index);
        const comparisonDate = shiftInputDate(formatDateOnly(report.previousFrom), index);
        return [
            currentDate,
            isSearchDataAvailableOn(report, currentDate)
                ? currentCounts.get(currentDate) ?? 0
                : '',
            ...(report.comparisonEnabled
                ? [
                    comparisonDate,
                    isSearchDataAvailableOn(report, comparisonDate)
                        ? comparisonCounts.get(comparisonDate) ?? 0
                        : '',
                ]
                : []),
        ];
    });
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-daily-search-volume-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadTopSearchesCsv = (report: StorefrontSearchReport, searches: StorefrontSearchReportTopSearch[]) => {
    const comparisonComplete = isComparisonDataComplete(report);
    const preferredKeys = ['year', 'make', 'model', 'submodel'];
    const discoveredKeys = Array.from(new Set(
        searches.flatMap((search) => Object.keys(search.fieldValues ?? {}))
    ));
    const fieldKeys = [
        ...preferredKeys.filter((key) => discoveredKeys.includes(key)),
        ...discoveredKeys.filter((key) => !preferredKeys.includes(key)).sort(),
    ];
    const headings = [
        'Vehicle',
        ...fieldKeys.map(formatFieldName),
        'Searches',
        'Search share (%)',
        ...(report.comparisonEnabled
            ? [
                comparisonComplete ? 'Comparison searches' : 'Comparison searches (partial)',
                'Change',
            ]
            : []),
        'Last searched',
    ];
    const rows = searches.map((search) => [
        getSearchLabel(search),
        ...fieldKeys.map((key) => search.fieldValues?.[key] ?? ''),
        search.searchCount,
        csvSharePercent(search.searchCount, report.totalSearches),
        ...(report.comparisonEnabled
            ? [
                search.previousSearchCount,
                comparisonComplete ? search.searchCount - search.previousSearchCount : '',
            ]
            : []),
        formatDateOnly(search.lastSearchedOn),
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-top-vehicle-searches-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadCoverageCsv = (
    report: StorefrontSearchReport,
    opportunities: StorefrontSearchReportTopSearch[],
    threshold: number
) => {
    const headings = ['Vehicle', 'Search demand', 'Search share (%)', 'Current mapped products', 'Last searched', 'Coverage status', 'Low coverage threshold (inclusive)'];
    const rows = opportunities.map((search) => [
        getSearchLabel(search),
        search.searchCount,
        csvSharePercent(search.searchCount, report.totalSearches),
        search.currentMappedProductCount,
        formatDateOnly(search.lastSearchedOn),
        search.currentMappedProductCount === 0 ? 'No mappings' : 'Low coverage',
        threshold,
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-coverage-opportunities-${formatDateOnly(report.from)}-to-${formatDateOnly(report.to)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const downloadFitmentCoverageCsv = (
    report: FitmentCoverageHealthReport,
    vehicles: FitmentCoverageHealthVehicle[]
) => {
    const headings = ['Vehicle', 'Current mapped products', 'Coverage status', 'Low coverage threshold (inclusive)'];
    const rows = vehicles.map((vehicle) => [
        getSearchLabel(vehicle),
        vehicle.currentMappedProductCount,
        vehicle.currentMappedProductCount === 0 ? 'No mappings' : 'Low coverage',
        report.lowCoverageThreshold,
    ]);
    const csv = [headings, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `partfits-fitment-coverage-health-${formatDateOnly(report.generatedAt)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
};

const getPercentChange = (current: number, previous: number) => {
    if (previous === 0) return current === 0 ? 0 : null;
    return ((current - previous) / previous) * 100;
};

const formatPercentChange = (current: number, previous: number) => {
    const percent = getPercentChange(current, previous);
    if (percent == null) return 'New';
    const rounded = Math.round(percent);
    return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString()}%`;
};

const getInclusiveDayCount = (report?: StorefrontSearchReport) => {
    if (!report?.from || !report.to) return 30;

    const from = new Date(`${report.from.slice(0, 10)}T00:00:00`);
    const to = new Date(`${report.to.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 30;

    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1);
};

const buildDailyBreakdownRows = (
    report: StorefrontSearchReport | undefined,
    days: number
): DailyBreakdownRow[] => {
    if (!report?.to || days <= 0) return [];

    const currentCounts = new Map(
        (report.dailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const comparisonCounts = new Map(
        (report.previousDailyCounts ?? []).map((day) => [formatDateOnly(day.date), day.searchCount])
    );
    const currentEnd = formatDateOnly(report.to);
    const comparisonEnd = formatDateOnly(report.previousTo);

    return Array.from({ length: days }, (_, index) => {
        const offset = -(days - index - 1);
        const currentDate = shiftInputDate(currentEnd, offset);
        const comparisonDate = report.comparisonEnabled
            ? shiftInputDate(comparisonEnd, offset)
            : undefined;

        return {
            currentDate,
            currentSearches: currentCounts.get(currentDate) ?? 0,
            currentDataAvailable: isSearchDataAvailableOn(report, currentDate),
            comparisonDate,
            comparisonSearches: comparisonDate
                ? comparisonCounts.get(comparisonDate) ?? 0
                : undefined,
            comparisonDataAvailable: comparisonDate
                ? isSearchDataAvailableOn(report, comparisonDate)
                : undefined,
        };
    });
};


type ReportSummaryMetricProps = {
    label: string;
    value: number | string;
    comparisonValue?: number;
    showComparison?: boolean;
    detail?: string;
};

const ReportSummaryMetric = ({
    label,
    value,
    comparisonValue,
    showComparison = false,
    detail,
}: ReportSummaryMetricProps) => {
    const numericValue = typeof value === 'number' ? value : undefined;
    const percentChange = showComparison
        && numericValue !== undefined
        && comparisonValue !== undefined
        ? comparisonValue === 0
            ? numericValue === 0 ? 0 : null
            : Math.round(((numericValue - comparisonValue) / comparisonValue) * 100)
        : undefined;
    const changeLabel = percentChange === null
        ? 'New'
        : percentChange === undefined
            ? undefined
            : `${percentChange > 0 ? '+' : ''}${percentChange}%`;
    const accessibleChange = percentChange === null
        ? 'New compared with the selected comparison period'
        : percentChange === 0
            ? 'No change from the selected comparison period'
            : percentChange === undefined
                ? undefined
                : `${Math.abs(percentChange)} percent ${percentChange > 0 ? 'increase' : 'decrease'} from the selected comparison period`;

    return (
        <s-stack gap="small-300">
            <s-text color="subdued">{label}</s-text>
            <s-stack direction="inline" gap="small-200" alignItems="baseline">
                <s-text fontSize="large-100" fontWeight="bold">{typeof value === 'number' ? formatCount(value) : value}</s-text>
                {changeLabel && <s-text accessibilityVisibility="hidden"
                    tone={!percentChange ? 'auto' : percentChange < 0 ? 'critical' : 'success'}>{changeLabel}</s-text>}
                {changeLabel && <s-text accessibilityVisibility="exclusive">{accessibleChange}</s-text>}
            </s-stack>
            {detail && <s-text color="subdued">{detail}</s-text>}
        </s-stack>
    );
};

const DailySearchVolume = ({ report, inProgressDate }: {
    report?: StorefrontSearchReport;
    inProgressDate?: string;
}) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const days = getInclusiveDayCount(report);
    const chartDays = Math.min(30, days);
    const rows = buildDailyBreakdownRows(report, days).reverse();
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * pageSize;
    const visibleRows = rows.slice(start, start + pageSize);
    const comparison = Boolean(report?.comparisonEnabled);
    const tableEvents = usePolarisEvents({
        nextpage: () => setPage(Math.min(pageCount, safePage + 1)),
        previouspage: () => setPage(Math.max(1, safePage - 1)),
    });
    const dateLabel = (date?: string) => `${formatSearchDateLabel(date)}, ${date?.slice(0, 4)}`;

    useEffect(() => setPage(1), [report, pageSize]);

    return <>
        <s-section accessibilityLabel="Daily search volume">
            <s-stack gap="base">
                <s-query-container>
                    <s-grid gridTemplateColumns="@container (inline-size > 480px) 1fr auto, 1fr" gap="small-200" alignItems="center">
                        <s-heading>Daily search volume</s-heading>
                        <div className="pf-report-legend" aria-label="Chart legend">
                            <span><i className="pf-report-legend-current" aria-hidden="true" />Current period</span>
                            {comparison && <span><i className="pf-report-legend-previous" aria-hidden="true" />Comparison period</span>}
                        </div>
                    </s-grid>
                </s-query-container>
                <div className="pf-report-chart">
                    <div className="pf-report-chart-unit">Searches</div>
                    <SearchActivityChart report={report} days={chartDays} showComparison={comparison}
                        variant="line" embedded inProgressDate={inProgressDate} />
                </div>
            </s-stack>
        </s-section>
        <s-section accessibilityLabel="Daily breakdown">
            <s-stack gap="base">
                <s-stack direction="inline" gap="small-200" alignItems="baseline">
                    <s-heading>Daily breakdown</s-heading>
                    <s-text color="subdued">{`${rows.length} days`}</s-text>
                </s-stack>
                {comparison && !rows.every(day => day.comparisonDataAvailable) && <s-banner tone="info">
                    {report?.dataAvailableFrom
                        ? `Search tracking for this store begins on ${formatDisplayDate(report.dataAvailableFrom)}. Earlier comparison dates are marked as no data and are not treated as zero-search days.`
                        : 'Some comparison dates have no search data. They are marked as no data and are not treated as zero-search days.'}
                </s-banner>}
                <s-table ref={tableEvents} paginate={pageCount > 1}
                    hasPreviousPage={safePage > 1} hasNextPage={safePage < pageCount}>
                    <s-table-header-row>
                        <s-table-header listSlot="primary">Date</s-table-header>
                        {comparison && <s-table-header listSlot="labeled">Compared with</s-table-header>}
                        <s-table-header listSlot="labeled" format="numeric">Searches</s-table-header>
                        {comparison && <s-table-header listSlot="labeled" format="numeric">Comparison</s-table-header>}
                        {comparison && <s-table-header listSlot="labeled" format="numeric">Change</s-table-header>}
                    </s-table-header-row>
                    <s-table-body>
                        {visibleRows.map(day => {
                            const inProgress = day.currentDate === inProgressDate;
                            const comparable = day.currentDataAvailable && day.comparisonDataAvailable && !inProgress;
                            return (
                                <s-table-row key={day.currentDate}>
                                    <s-table-cell>
                                        {`${dateLabel(day.currentDate)}${inProgress && !comparison ? ' · In progress' : ''}`}
                                    </s-table-cell>
                                    {comparison && <s-table-cell>{dateLabel(day.comparisonDate)}</s-table-cell>}
                                    <s-table-cell>{day.currentDataAvailable ? formatCount(day.currentSearches) : 'No data'}</s-table-cell>
                                    {comparison && <s-table-cell>
                                        {day.comparisonDataAvailable ? formatCount(day.comparisonSearches) : 'No data'}
                                    </s-table-cell>}
                                    {comparison && <s-table-cell>
                                        {inProgress ? 'In progress' : comparable ? formatPercentChange(day.currentSearches, day.comparisonSearches ?? 0) : 'No data'}
                                    </s-table-cell>}
                                </s-table-row>
                            );
                        })}
                    </s-table-body>
                </s-table>
                {rows.length > 0 && <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                    <s-text color="subdued">{`${start + 1}–${Math.min(start + pageSize, rows.length)} of ${rows.length} days`}</s-text>
                    <s-select label="Rows per page" labelAccessibilityVisibility="exclusive" value={String(pageSize)}
                        onChange={event => setPageSize(Number(fieldValue(event)))}>
                        {pageSizeOptions.map(value => <s-option key={value} value={value}>{`${value} per page`}</s-option>)}
                    </s-select>
                </s-grid>}
            </s-stack>
        </s-section>
    </>;
};

const aboutReportId = 'pf-report-about';
const rangePickerId = 'pf-report-range-picker';
const comparePickerId = 'pf-report-compare-picker';

// The calendar shows two months on wide screens, like the admin's analytics date pickers.
const useTwoMonthCalendar = () => {
    const query = '(min-width: 720px)';
    const [wide, setWide] = useState(() => window.matchMedia?.(query)?.matches ?? true);
    useEffect(() => {
        const media = window.matchMedia?.(query);
        if (!media) return;
        const update = () => setWide(media.matches);
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);
    return wide;
};

// First month to show so the end of the selected range is visible.
const pickerView = (to: string, twoMonths: boolean) => {
    const date = parseInputDate(to);
    date.setDate(1);
    if (twoMonths) date.setMonth(date.getMonth() - 1);
    return toInputDate(date).slice(0, 7);
};

const PickerOption = ({ label, selected, onSelect, hideTarget }: {
    label: string;
    selected: boolean;
    onSelect: () => void;
    hideTarget?: string;
}) => (
    <s-clickable onClick={onSelect} paddingBlock="small-200" paddingInline="small" borderRadius="base"
        background={selected ? 'strong' : 'transparent'}
        commandFor={hideTarget} command={hideTarget ? '--hide' : undefined}>
        <s-text fontWeight={selected ? 'semibold' : 'base'}>{label}</s-text>
    </s-clickable>
);

/** Presets on the left; fields, calendar and actions on the right, as in the admin's analytics pickers. */
const DateRangePicker = ({ wide, options, panel, footer }: {
    wide: boolean;
    options: ReactNode;
    panel?: ReactNode;
    footer?: ReactNode;
}) => (
    // Fixed widths on wide screens; otherwise the calendar stretches to fill the whole popover.
    <s-grid gridTemplateColumns={!wide ? 'auto' : panel ? '180px 560px' : '260px'}>
        <s-box background="subdued" padding="small">
            <s-stack gap="none">{options}</s-stack>
        </s-box>
        {panel && (
            <s-stack gap="none">
                <s-box padding="base">{panel}</s-box>
                <s-divider />
                <s-box padding="base">
                    <s-stack direction="inline" justifyContent="end" alignItems="center" gap="small-200">{footer}</s-stack>
                </s-box>
            </s-stack>
        )}
    </s-grid>
);

const ReportsPage = () => {
    const [initialDates] = useState(getInitialDateRange);
    const [report, setReport] = useState<StorefrontSearchReport>();
    const [coverageHealth, setCoverageHealth] = useState<FitmentCoverageHealthReport>();
    const [selectedReport, setSelectedReport] = useState<ReportKind>(getInitialReport);
    const [vehicleSort, setVehicleSort] = useState<VehicleSort>(() =>
        new URLSearchParams(window.location.search).get('report') === 'search_demand_trends' ? 'increase' : 'searches');
    const [range, setRange] = useState<ReportRange>(initialDates.range);
    const [appliedRange, setAppliedRange] = useState<ReportRange>(initialDates.range);
    const [fromDate, setFromDate] = useState(initialDates.from);
    const [toDate, setToDate] = useState(initialDates.to);
    const [comparison, setComparison] = useState<ComparisonPeriod>('previous_period');
    const [comparisonFromDate, setComparisonFromDate] = useState(initialDates.comparisonFrom);
    const [comparisonToDate, setComparisonToDate] = useState(initialDates.comparisonTo);
    const twoMonths = useTwoMonthCalendar();
    const [draftRange, setDraftRange] = useState<ReportRange>(initialDates.range);
    const [draftFrom, setDraftFrom] = useState(initialDates.from);
    const [draftTo, setDraftTo] = useState(initialDates.to);
    // Latest draft dates, read by the calendar's change events (which can run with stale state).
    const draftDates = useRef({ from: initialDates.from, to: initialDates.to });
    const [rangeView, setRangeView] = useState(() => pickerView(initialDates.to, twoMonths));
    const [draftComparison, setDraftComparison] = useState<ComparisonPeriod>('previous_period');
    const [compareView, setCompareView] = useState(() => pickerView(initialDates.comparisonTo, twoMonths));
    const [draftComparisonRange, setDraftComparisonRange] = useState<ComparisonDateRange>({
        start: parseInputDate(initialDates.comparisonFrom),
        end: parseInputDate(initialDates.comparisonTo),
    });
    const [loading, setLoading] = useState(true);
    const [coverageLoading, setCoverageLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [coverageError, setCoverageError] = useState<string>();
    const [coverageThreshold, setCoverageThreshold] = useState<number>();
    const [thresholdDraft, setThresholdDraft] = useState('5');
    const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>('all');
    const [settingsError, setSettingsError] = useState<string>();
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const coverageRequestVersion = useRef(0);
    const isCoverageReport = selectedReport === 'coverage_opportunities' || selectedReport === 'fitment_coverage_health';
    const parsedThreshold = Number(thresholdDraft);
    const thresholdValid = /^\d+$/.test(thresholdDraft) && Number.isInteger(parsedThreshold)
        && parsedThreshold >= 1 && parsedThreshold <= 2147483647;
    const reportRequestVersion = useRef(0);

    const loadCoverageSettings = useCallback(async () => {
        setSettingsLoading(true);
        setSettingsError(undefined);
        try {
            const settings = await agent.Reports.coverageSettings();
            setCoverageThreshold(settings.lowCoverageThreshold);
            setThresholdDraft(String(settings.lowCoverageThreshold));
        } catch (requestError) {
            setSettingsError(await getApiErrorMessage(requestError, 'Unable to load coverage settings.'));
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    useEffect(() => { void loadCoverageSettings(); }, [loadCoverageSettings]);

    const saveCoverageThreshold = async () => {
        if (!thresholdValid) return;
        setSettingsSaving(true);
        setSettingsError(undefined);
        try {
            const settings = await agent.Reports.updateCoverageSettings({ lowCoverageThreshold: parsedThreshold });
            ++coverageRequestVersion.current;
            setCoverageHealth(undefined);
            setCoverageError(undefined);
            setCoverageThreshold(settings.lowCoverageThreshold);
            setThresholdDraft(String(settings.lowCoverageThreshold));
        } catch (requestError) {
            setSettingsError(await getApiErrorMessage(requestError, 'Unable to save coverage threshold.'));
        } finally {
            setSettingsSaving(false);
        }
    };

    const loadReport = useCallback(async (
        selectedRange: ReportRange,
        selectedFrom: string,
        selectedTo: string,
        selectedComparison: ComparisonPeriod,
        selectedComparisonFrom: string,
        selectedComparisonTo: string
    ) => {
        const requestVersion = ++reportRequestVersion.current;
        if (selectedRange === 'custom' && (!selectedFrom || !selectedTo || selectedFrom > selectedTo)) {
            setLoading(false);
            setError('Choose a valid custom date range.');
            return;
        }

        if (selectedComparison === 'custom') {
            if (!selectedComparisonFrom || !selectedComparisonTo || selectedComparisonFrom > selectedComparisonTo) {
                setLoading(false);
                setError('Choose a valid custom comparison period.');
                return;
            }

            const selectedPeriodDays = selectedRange === 'custom'
                ? getInputDateRangeDays(selectedFrom, selectedTo)
                : Number(selectedRange);
            const comparisonPeriodDays = getInputDateRangeDays(
                selectedComparisonFrom,
                selectedComparisonTo
            );
            if (comparisonPeriodDays !== selectedPeriodDays) {
                setLoading(false);
                setError(`Choose a comparison period that is also ${selectedPeriodDays} days long.`);
                return;
            }
        }

        setLoading(true);
        setError(undefined);
        try {
            const response = await agent.Reports.searchActivity(selectedRange === 'custom'
                ? {
                    from: selectedFrom,
                    to: selectedTo,
                    comparison: selectedComparison,
                    comparisonFrom: selectedComparison === 'custom' ? selectedComparisonFrom : undefined,
                    comparisonTo: selectedComparison === 'custom' ? selectedComparisonTo : undefined,
                    limit: 250,
                }
                : {
                    days: Number(selectedRange),
                    comparison: selectedComparison,
                    comparisonFrom: selectedComparison === 'custom' ? selectedComparisonFrom : undefined,
                    comparisonTo: selectedComparison === 'custom' ? selectedComparisonTo : undefined,
                    limit: 250,
                });
            if (requestVersion !== reportRequestVersion.current) return;
            setReport(response);
            setAppliedRange(selectedRange);
        } catch (requestError) {
            const message = await getApiErrorMessage(requestError, 'Unable to load reports.');
            if (requestVersion === reportRequestVersion.current) setError(message);
        } finally {
            if (requestVersion === reportRequestVersion.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadReport(
            initialDates.range,
            initialDates.from,
            initialDates.to,
            'previous_period',
            initialDates.comparisonFrom,
            initialDates.comparisonTo
        );
    }, [initialDates, loadReport]);

    const loadCoverageHealth = useCallback(async () => {
        if (coverageThreshold === undefined) return;
        const version = ++coverageRequestVersion.current;
        setCoverageLoading(true);
        setCoverageError(undefined);
        try {
            const response = await agent.Reports.fitmentCoverage(250, coverageThreshold, coverageFilter);
            if (version === coverageRequestVersion.current) setCoverageHealth(response);
        } catch (requestError) {
            const message = await getApiErrorMessage(requestError, 'Unable to load fitment coverage health.');
            if (version === coverageRequestVersion.current) setCoverageError(message);
        } finally {
            if (version === coverageRequestVersion.current) setCoverageLoading(false);
        }
    }, [coverageThreshold, coverageFilter]);

    useEffect(() => {
        if (selectedReport === 'fitment_coverage_health') {
            setCoverageHealth(undefined);
            void loadCoverageHealth();
        }
        return () => { coverageRequestVersion.current += 1; };
    }, [loadCoverageHealth, selectedReport]);

    const topSearches = report?.topSearches ?? [];
    const coverageOpportunities = [...topSearches]
        .filter((search) => coverageThreshold !== undefined && search.currentMappedProductCount <= coverageThreshold)
        .sort((left, right) => {
            const leftHasNoResults = left.currentMappedProductCount === 0;
            const rightHasNoResults = right.currentMappedProductCount === 0;

            if (leftHasNoResults !== rightHasNoResults) {
                return leftHasNoResults ? -1 : 1;
            }

            return right.searchCount - left.searchCount;
        });
    const noResultOpportunities = coverageOpportunities.filter((search) => search.currentMappedProductCount === 0);
    const lowCoverageOpportunities = coverageOpportunities.filter((search) => search.currentMappedProductCount > 0);
    const visibleOpportunities = coverageOpportunities.filter(search => coverageFilter === 'all'
        || (coverageFilter === 'unmapped' ? search.currentMappedProductCount === 0 : search.currentMappedProductCount > 0));
    const noResultSearchDemand = noResultOpportunities.reduce(
        (total, search) => total + search.searchCount,
        0
    );
    const comparisonDataComplete = isComparisonDataComplete(report);
    const canSortByChange = Boolean(report?.comparisonEnabled && comparisonDataComplete);
    const effectiveVehicleSort = canSortByChange ? vehicleSort : 'searches';
    const sortedSearches = [...topSearches]
        .sort((left, right) => {
            if (effectiveVehicleSort === 'searches') {
                return right.searchCount - left.searchCount;
            }

            const changeDifference = (right.searchCount - right.previousSearchCount)
                - (left.searchCount - left.previousSearchCount);
            return (effectiveVehicleSort === 'decrease' ? -changeDifference : changeDifference)
                || right.searchCount - left.searchCount;
        });
    const coverageHealthRows = coverageHealth?.vehicles ?? [];
    // Preset ranges end on the shop's current day, as determined by the API.
    const inProgressDate = appliedRange !== 'custom' ? report?.to.slice(0, 10) : undefined;
    const supportsComparison = comparisonReportKinds.has(selectedReport);
    const detailKey = JSON.stringify([selectedReport, report?.generatedAt, report?.from, report?.to,
        report?.comparison, report?.previousFrom, report?.previousTo, coverageHealth?.generatedAt, coverageThreshold, coverageFilter, effectiveVehicleSort]);
    const comparisonNotice = report?.comparisonEnabled && !comparisonDataComplete ? (
        <s-banner tone="info">
            {report.dataAvailableFrom
                ? `Comparison totals are partial because this period begins before search tracking started on ${formatDisplayDate(report.dataAvailableFrom)}. Changes are withheld.`
                : 'Comparison totals are partial because search data is missing for part of the comparison period. Changes are withheld.'}
        </s-banner>
    ) : undefined;
    const fitmentCoverageRate = coverageHealth?.totalVehicles
        ? (coverageHealth.vehiclesWithMappings / coverageHealth.totalVehicles) * 100
        : 0;

    const getDefaultCustomComparisonRange = (): ComparisonDateRange => {
        const selectedPeriodDays = range === 'custom'
            ? getInputDateRangeDays(fromDate, toDate)
            : Number(range);
        const currentPeriodStart = range === 'custom'
            ? fromDate
            : shiftInputDate(formatDateOnly(report?.to ?? initialToDate), -(selectedPeriodDays - 1));
        const defaultComparisonTo = shiftInputDate(currentPeriodStart, -1);
        const defaultComparisonFrom = shiftInputDate(
            defaultComparisonTo,
            -(selectedPeriodDays - 1)
        );

        return {
            start: parseInputDate(defaultComparisonFrom),
            end: parseInputDate(defaultComparisonTo),
        };
    };

    const presetRange = (preset: ReportRange) => ({
        from: shiftInputDate(initialToDate, -(Number(preset) - 1)),
        to: initialToDate,
    });

    const openRangePicker = () => {
        const dates = range === 'custom' ? { from: fromDate, to: toDate } : presetRange(range);
        setDraftRange(range);
        setDraftDates(dates.from, dates.to);
        setRangeView(pickerView(dates.to, twoMonths));
    };

    const chooseDraftRange = (value: ReportRange) => {
        setDraftRange(value);
        if (value === 'custom') return;
        const dates = presetRange(value);
        setDraftDates(dates.from, dates.to);
        setRangeView(pickerView(dates.to, twoMonths));
    };

    const setDraftDates = (from: string, to: string) => {
        draftDates.current = { from, to };
        setDraftFrom(from);
        setDraftTo(to);
    };

    const editDraftRange = (dates: { from?: string; to?: string }) => {
        const next = { ...draftDates.current, ...dates };
        // The calendar and fields also report values set from code, such as a chosen preset.
        if (next.from === draftDates.current.from && next.to === draftDates.current.to) return;
        setDraftRange('custom');
        setDraftDates(next.from, next.to);
    };

    const draftRangeValid = draftRange !== 'custom' || Boolean(draftFrom && draftTo && draftFrom <= draftTo);

    const applyRange = () => {
        if (!draftRangeValid) return;
        setRange(draftRange);
        if (draftRange === 'custom') {
            setFromDate(draftFrom);
            setToDate(draftTo);
        }
        void loadReport(draftRange, draftFrom, draftTo, supportsComparison ? comparison : 'previous_period', comparisonFromDate, comparisonToDate);
    };

    const openComparePicker = () => {
        const dates = comparison === 'custom'
            ? { start: parseInputDate(comparisonFromDate), end: parseInputDate(comparisonToDate) }
            : getDefaultCustomComparisonRange();
        setDraftComparison(comparison);
        setDraftComparisonRange(dates);
        setCompareView(pickerView(toInputDate(dates.end), twoMonths));
    };

    const chooseComparison = (value: ComparisonPeriod) => {
        setDraftComparison(value);
        if (value === 'custom') return;
        setComparison(value);
        void loadReport(range, fromDate, toDate, value, comparisonFromDate, comparisonToDate);
    };

    const expectedComparisonDays = range === 'custom'
        ? getInputDateRangeDays(fromDate, toDate)
        : Number(range);
    const draftComparisonDays = getInputDateRangeDays(
        toInputDate(draftComparisonRange.start),
        toInputDate(draftComparisonRange.end)
    );
    const customComparisonIsValid = expectedComparisonDays > 0
        && draftComparisonDays === expectedComparisonDays;
    const currentPeriodStart = range === 'custom'
        ? fromDate
        : shiftInputDate(
            formatDateOnly(report?.to ?? initialToDate),
            -(Math.max(1, expectedComparisonDays) - 1)
        );
    const latestComparisonDate = shiftInputDate(currentPeriodStart, -1);

    const applyComparisonPicker = () => {
        if (!customComparisonIsValid) return;
        const from = toInputDate(draftComparisonRange.start);
        const to = toInputDate(draftComparisonRange.end);
        setComparison('custom');
        setComparisonFromDate(from);
        setComparisonToDate(to);
        void loadReport(range, fromDate, toDate, 'custom', from, to);
    };

    const rangePickerEvents = usePolarisEvents({
        viewchange: event => setRangeView((event.currentTarget as HTMLElement & { view?: string }).view ?? rangeView),
    });
    const comparePickerEvents = usePolarisEvents({
        viewchange: event => setCompareView((event.currentTarget as HTMLElement & { view?: string }).view ?? compareView),
    });

    const selectedReportLabel = reportDefinitions.find(
        (definition) => definition.value === selectedReport
    )?.label ?? 'Report';
    const reportDays = getInclusiveDayCount(report);
    const shareContext = `Shares use all ${formatCount(report?.totalSearches)} recorded searches in the selected period.`;
    const thresholdContext = `The low coverage threshold is saved for this store and shared by both coverage reports. Zero mapped products are always “No mappings”.${coverageThreshold === undefined ? '' : ` Current threshold: ${coverageThreshold} or fewer.`} Show filters the list and CSV; summary metrics include both groups.`;
    // Report notes live behind the About button to keep the page itself uncluttered.
    const aboutNotes = {
        daily_search_volume: reportDays > 30
            ? [`Chart shows the most recent 30 days. Daily breakdown and CSV include all ${reportDays} days.`]
            : [],
        top_vehicle_searches: [
            'Includes up to 250 of the most searched vehicle combinations. Change sorting ranks these vehicles by the difference in search counts. New means no searches in the comparison period.',
            shareContext,
            ...(canSortByChange ? [] : ['Change sorting needs a complete comparison period.']),
        ],
        coverage_opportunities: [
            `No mappings first, then 1–${coverageThreshold} mapped products, ranked by searches. Metrics and results use current mappings for up to 250 of the most searched vehicles. List and CSV respect the Show filter.`,
            shareContext,
            thresholdContext,
        ],
        fitment_coverage_health: [
            `Up to 250 vehicles with no mappings or 1–${coverageThreshold} mapped products, using the Show filter. Unmapped vehicles appear first. Summary metrics cover the full active catalog.`,
            thresholdContext,
        ],
    }[selectedReport];

    const exportDisabled = selectedReport === 'fitment_coverage_health'
        ? coverageHealthRows.length === 0
        : selectedReport === 'coverage_opportunities'
            ? visibleOpportunities.length === 0
            : selectedReport === 'daily_search_volume'
                ? !report?.dailyCounts.length
                : !report?.topSearches.length;
    const exportReport = () => {
        if (selectedReport === 'coverage_opportunities') {
            if (!report) return;
            if (coverageThreshold === undefined) return;
            downloadCoverageCsv(report, visibleOpportunities, coverageThreshold);
            return;
        }

        if (selectedReport === 'fitment_coverage_health') {
            if (!coverageHealth) return;
            downloadFitmentCoverageCsv(coverageHealth, coverageHealthRows);
            return;
        }

        if (!report) return;
        if (selectedReport === 'daily_search_volume') {
            downloadDailyVolumeCsv(report);
            return;
        }

        downloadTopSearchesCsv(report, sortedSearches);
    };

    // Like the admin's analytics pickers, the buttons show the dates the report covers.
    const rangeLabel = report
        ? formatButtonRange(report.from, report.to)
        : range === 'custom'
            ? formatButtonRange(fromDate, toDate)
            : rangeOptions.find(option => option.value === range)?.label ?? 'Date range';
    const comparisonLabel = report
        ? report.comparisonEnabled ? formatButtonRange(report.previousFrom, report.previousTo) : 'No comparison'
        : comparisonOptions.find(option => option.value === comparison)?.label ?? 'Compare';

    const rangePicker = (
        <s-popover id={rangePickerId}>
            <DateRangePicker
                wide={twoMonths}
                options={rangeOptions.map(option => (
                    <PickerOption key={option.value} label={option.label} selected={draftRange === option.value}
                        onSelect={() => chooseDraftRange(option.value as ReportRange)} />
                ))}
                panel={(
                    <s-stack gap="base">
                        <s-grid gridTemplateColumns={twoMonths ? '1fr auto 1fr' : '1fr'} gap="small-200" alignItems="center">
                            <s-date-field label="From" labelAccessibilityVisibility="exclusive" value={draftFrom}
                                allow={`--${draftTo || initialToDate}`}
                                onChange={event => editDraftRange({ from: fieldValue(event) })} />
                            {twoMonths && <s-icon type="arrow-right" />}
                            <s-date-field label="To" labelAccessibilityVisibility="exclusive" value={draftTo}
                                allow={`${draftFrom}--${initialToDate}`}
                                onChange={event => editDraftRange({ to: fieldValue(event) })} />
                        </s-grid>
                        <s-date-picker ref={rangePickerEvents} type="range" visibleMonths={twoMonths ? '2' : '1'}
                            value={`${draftFrom}--${draftTo}`} view={rangeView}
                            disallow={`${shiftInputDate(initialToDate, 1)}--`}
                            onChange={event => {
                                const [start, end] = fieldValue(event).split('--');
                                if (start) editDraftRange({ from: start, to: end || start });
                            }} />
                    </s-stack>
                )}
                footer={<>
                    <s-button commandFor={rangePickerId} command="--hide">Cancel</s-button>
                    <s-button variant="primary" disabled={!draftRangeValid}
                        commandFor={rangePickerId} command="--hide" onClick={applyRange}>
                        Apply
                    </s-button>
                </>}
            />
        </s-popover>
    );

    const comparePicker = (
        <s-popover id={comparePickerId}>
            <DateRangePicker
                wide={twoMonths}
                options={comparisonOptions.map(option => (
                    <PickerOption key={option.value} label={option.label} selected={draftComparison === option.value}
                        hideTarget={option.value === 'custom' ? undefined : comparePickerId}
                        onSelect={() => chooseComparison(option.value as ComparisonPeriod)} />
                ))}
                panel={draftComparison === 'custom' ? (
                    <s-stack gap="base">
                        <s-grid gridTemplateColumns={twoMonths ? '1fr auto 1fr' : '1fr'} gap="small-200" alignItems="center">
                            <s-date-field label="Comparison from" labelAccessibilityVisibility="exclusive"
                                value={toInputDate(draftComparisonRange.start)} allow={`--${latestComparisonDate}`}
                                onChange={event => setDraftComparisonRange(current => ({ ...current, start: parseInputDate(fieldValue(event)) }))} />
                            {twoMonths && <s-icon type="arrow-right" />}
                            <s-date-field label="Comparison to" labelAccessibilityVisibility="exclusive"
                                value={toInputDate(draftComparisonRange.end)} allow={`--${latestComparisonDate}`}
                                onChange={event => setDraftComparisonRange(current => ({ ...current, end: parseInputDate(fieldValue(event)) }))} />
                        </s-grid>
                        <s-date-picker ref={comparePickerEvents} type="range" visibleMonths={twoMonths ? '2' : '1'}
                            value={`${toInputDate(draftComparisonRange.start)}--${toInputDate(draftComparisonRange.end)}`}
                            view={compareView} disallow={`${shiftInputDate(latestComparisonDate, 1)}--`}
                            onChange={event => {
                                const [start, end] = fieldValue(event).split('--');
                                if (start) setDraftComparisonRange({ start: parseInputDate(start), end: parseInputDate(end || start) });
                            }} />
                    </s-stack>
                ) : undefined}
                footer={<>
                    <s-text tone={customComparisonIsValid ? 'auto' : 'critical'}>
                        {customComparisonIsValid
                            ? `${draftComparisonDays} days`
                            : `Select ${expectedComparisonDays} days (${draftComparisonDays} selected)`}
                    </s-text>
                    <s-button commandFor={comparePickerId} command="--hide">Cancel</s-button>
                    <s-button variant="primary" disabled={!customComparisonIsValid}
                        commandFor={comparePickerId} command="--hide" onClick={applyComparisonPicker}>
                        Apply
                    </s-button>
                </>}
            />
        </s-popover>
    );

    const showLoading = (isCoverageReport && settingsLoading) || (selectedReport === 'fitment_coverage_health'
        ? coverageLoading && !coverageHealth
        : loading && !report);
    const hideResults = (isCoverageReport && coverageThreshold === undefined)
        || (selectedReport === 'fitment_coverage_health' && !coverageHealth);

    const results = selectedReport === 'daily_search_volume' || selectedReport === 'top_vehicle_searches' ? (
        <>
            <s-section accessibilityLabel="Search summary">
                <s-query-container>
                    <s-grid gridTemplateColumns="@container (inline-size > 520px) 1fr 1fr 1fr, 1fr" gap="base">
                        <ReportSummaryMetric
                            label="Total searches"
                            value={report?.totalSearches ?? 0}
                            comparisonValue={report?.previousTotalSearches ?? 0}
                            showComparison={Boolean(
                                report?.comparisonEnabled && comparisonDataComplete
                            )}
                        />
                        <ReportSummaryMetric
                            label="Unique vehicles searched"
                            value={report?.uniqueSearchCount ?? 0}
                            comparisonValue={report?.previousUniqueSearchCount ?? 0}
                            showComparison={Boolean(
                                report?.comparisonEnabled && comparisonDataComplete
                            )}
                        />
                        <ReportSummaryMetric
                            label="Searches today"
                            value={report?.searchesToday ?? 0}
                        />
                    </s-grid>
                </s-query-container>
            </s-section>
            {selectedReport === 'daily_search_volume' ? (
                <DailySearchVolume report={report} inProgressDate={inProgressDate} />
            ) : (
                <s-section accessibilityLabel="Top vehicle searches">
                    <VehicleReportDetails key={detailKey} mode="searches" title="Top vehicle searches"
                        rows={sortedSearches} totalSearches={report?.totalSearches} comparison={report?.comparisonEnabled}
                        comparisonComplete={comparisonDataComplete} notice={comparisonNotice}
                        controls={<s-box maxInlineSize="280px">
                            <s-select label="Sort by" value={effectiveVehicleSort}
                                onChange={event => setVehicleSort(fieldValue(event) as VehicleSort)}>
                                <s-option value="searches">Most searched</s-option>
                                <s-option value="increase" disabled={!canSortByChange}>Biggest increases</s-option>
                                <s-option value="decrease" disabled={!canSortByChange}>Biggest decreases</s-option>
                            </s-select>
                        </s-box>}
                        emptyMessage="No vehicle searches were recorded in this range." />
                </s-section>
            )}
        </>
    ) : selectedReport === 'coverage_opportunities' ? (
        <>
            <s-section accessibilityLabel="Coverage summary">
                <s-query-container>
                    <s-grid gridTemplateColumns="@container (inline-size > 520px) 1fr 1fr 1fr, 1fr" gap="base">
                        <ReportSummaryMetric label="Vehicles with no mappings" value={noResultOpportunities.length} />
                        <ReportSummaryMetric label="Searches with no mappings" value={noResultSearchDemand}
                            detail={report && report.totalSearches > 0 ? `${formatSharePercent(getSharePercent(noResultSearchDemand, report.totalSearches))} of all recorded searches` : undefined} />
                        <ReportSummaryMetric label="Low-coverage vehicles" value={lowCoverageOpportunities.length}
                            detail={`1–${coverageThreshold} mapped products`} />
                    </s-grid>
                </s-query-container>
            </s-section>
            <s-section accessibilityLabel="Coverage opportunities">
                <VehicleReportDetails key={detailKey} mode="opportunities" title="Coverage opportunities"
                    rows={visibleOpportunities} totalSearches={report?.totalSearches}
                    emptyMessage={topSearches.length === 0
                        ? 'No vehicle searches were recorded in this range.'
                        : 'No searched vehicles match the saved threshold and Show filter.'} />
            </s-section>
        </>
    ) : (
        <>
            <s-section accessibilityLabel="Coverage summary">
                <s-query-container>
                    <s-grid gridTemplateColumns="@container (inline-size > 720px) 1fr 1fr 1fr 1fr, 1fr 1fr" gap="base">
                        <ReportSummaryMetric label="Active vehicles" value={coverageHealth?.totalVehicles ?? 0}
                            detail={coverageHealth ? `${coverageHealth.averageMappedProducts.toFixed(1)} products per covered vehicle` : undefined} />
                        <ReportSummaryMetric label="Vehicles without mappings" value={coverageHealth?.vehiclesWithoutMappings ?? 0}
                            detail={coverageHealth && coverageHealth.totalVehicles > 0 ? `${formatSharePercent(getSharePercent(coverageHealth.vehiclesWithoutMappings, coverageHealth.totalVehicles))} of active vehicles` : undefined} />
                        <ReportSummaryMetric label="Low-coverage vehicles" value={coverageHealth?.lowCoverageVehicles ?? 0}
                            detail={`${coverageHealth && coverageHealth.totalVehicles > 0 ? `${formatSharePercent(getSharePercent(coverageHealth.lowCoverageVehicles, coverageHealth.totalVehicles))} of active vehicles · ` : ''}1–${coverageThreshold} mapped products`} />
                        <ReportSummaryMetric label="Coverage rate" value={`${fitmentCoverageRate.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`}
                            detail="At least one mapped product" />
                    </s-grid>
                </s-query-container>
            </s-section>
            <s-section accessibilityLabel="Vehicles needing attention">
                <VehicleReportDetails key={detailKey} mode="health" title="Vehicles needing attention"
                    rows={coverageHealthRows}
                    emptyMessage={(coverageHealth?.totalVehicles ?? 0) === 0
                        ? 'No active vehicles are available in the current fitment catalog.'
                        : 'No active vehicles match the saved threshold and Show filter.'} />
            </s-section>
        </>
    );

    return (
        <>
            <s-page heading={FEATURES.reports} inlineSize="large">
                <div className="pf-reports">
                    <s-stack gap="base">
                        <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                            <s-heading fontSize="large-200">{FEATURES.reports}</s-heading>
                            <s-stack direction="inline" gap="small-200">
                                <s-button icon="export" onClick={exportReport}
                                    disabled={exportDisabled || (isCoverageReport && (settingsLoading || settingsSaving || coverageThreshold === undefined)) || (selectedReport === 'fitment_coverage_health'
                                        ? coverageLoading || Boolean(coverageError)
                                        : loading || Boolean(error))}>
                                    Export CSV
                                </s-button>
                                <s-button icon="info" accessibilityLabel="About this report" commandFor={aboutReportId} />
                            </s-stack>
                        </s-grid>
                        <s-popover id={aboutReportId}>
                            <s-box padding="base" maxInlineSize="400px">
                                <s-stack gap="small-200">
                                    <s-heading>{selectedReportLabel}</s-heading>
                                    {aboutNotes.map(note => <s-paragraph key={note}>{note}</s-paragraph>)}
                                    <s-paragraph color="subdued">
                                        Data is aggregated at the store level and does not include customer, session, or device information.
                                    </s-paragraph>
                                </s-stack>
                            </s-box>
                        </s-popover>

                        {isCoverageReport && <PlanFeatureStatus feature="coverageReports" />}
                        {selectedReport !== 'fitment_coverage_health' && error && (
                            <s-banner tone="critical">
                                {error}
                                <s-button slot="secondary-actions" onClick={() => {
                                    void loadReport(range, fromDate, toDate, comparison, comparisonFromDate, comparisonToDate);
                                }}>Retry</s-button>
                            </s-banner>
                        )}
                        {selectedReport === 'fitment_coverage_health' && coverageError && (
                            <s-banner tone="critical">{coverageError}</s-banner>
                        )}

                        <s-stack gap="small-200">
                            <s-query-container>
                                <s-grid gridTemplateColumns="@container (inline-size > 640px) 280px 1fr, 1fr" gap="small-200" alignItems="center">
                                    <s-select label="Report" labelAccessibilityVisibility="exclusive" value={selectedReport}
                                        onChange={event => setSelectedReport(fieldValue(event) as ReportKind)}>
                                        {reportOptions.map(option => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                                    </s-select>
                                    <s-stack direction="inline" gap="small-200" alignItems="center">
                                        {selectedReport === 'fitment_coverage_health' ? (
                                            <s-button icon="refresh" onClick={loadCoverageHealth} loading={coverageLoading}
                                                disabled={coverageThreshold === undefined || settingsSaving}>
                                                Refresh
                                            </s-button>
                                        ) : <>
                                            <s-button icon="calendar" commandFor={rangePickerId} onClick={openRangePicker}
                                                accessibilityLabel={`Date range: ${rangeLabel}`}>
                                                {rangeLabel}
                                            </s-button>
                                            {supportsComparison && (
                                                <s-button icon="calendar-compare" commandFor={comparePickerId} onClick={openComparePicker}
                                                    accessibilityLabel={`Compare to: ${comparisonLabel}`}>
                                                    {comparisonLabel}
                                                </s-button>
                                            )}
                                            {supportsComparison && <PlanFeatureStatus feature="demandComparison" />}
                                        </>}
                                        {loading && selectedReport !== 'fitment_coverage_health' && (
                                            <s-spinner size="base" accessibilityLabel="Updating report" />
                                        )}
                                    </s-stack>
                                </s-grid>
                            </s-query-container>
                            {selectedReport !== 'fitment_coverage_health' && rangePicker}
                            {supportsComparison && comparePicker}
                            {isCoverageReport && settingsError && (
                                <s-banner tone="critical">
                                    {settingsError}
                                    {coverageThreshold === undefined && (
                                        <s-button slot="secondary-actions" onClick={() => { void loadCoverageSettings(); }}>Retry</s-button>
                                    )}
                                </s-banner>
                            )}
                            {isCoverageReport && (
                                <s-query-container>
                                    <s-grid gridTemplateColumns="@container (inline-size > 720px) 1fr auto 1fr, 1fr" gap="small-200" alignItems="end">
                                        <s-number-field label="Flag low coverage at" value={thresholdDraft} inputMode="numeric"
                                            min={1} max={2147483647} step={1} suffix="mapped products or fewer"
                                            disabled={settingsLoading || settingsSaving || coverageThreshold === undefined}
                                            error={!thresholdValid ? 'Enter a positive whole number (up to 2,147,483,647).' : undefined}
                                            onInput={event => setThresholdDraft(fieldValue(event))} />
                                        <s-button onClick={saveCoverageThreshold} loading={settingsSaving}
                                            disabled={settingsLoading || coverageThreshold === undefined || !thresholdValid || parsedThreshold === coverageThreshold}>
                                            Save threshold
                                        </s-button>
                                        <s-select label="Show" value={coverageFilter}
                                            onChange={event => setCoverageFilter(fieldValue(event) as CoverageFilter)}>
                                            <s-option value="all">All needing attention</s-option>
                                            <s-option value="unmapped">No mappings</s-option>
                                            <s-option value="low">Low coverage</s-option>
                                        </s-select>
                                    </s-grid>
                                </s-query-container>
                            )}
                        </s-stack>

                        {showLoading ? (
                            <s-section accessibilityLabel="Loading reports">
                                <s-stack alignItems="center">
                                    <s-spinner size="large" accessibilityLabel="Loading reports" />
                                </s-stack>
                            </s-section>
                        ) : hideResults ? null : results}
                    </s-stack>
                </div>
            </s-page>
        </>
    );
};

export default ReportsPage;
